import {
  AutocompleteProps as MuiAutoCompleteProps,
  AutocompleteRenderInputParams as MuiAutoCompleteRenderInputParams,
  default as MuiAutoComplete,
} from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { ReactElement, ReactNode } from 'react'
import { Field } from 'react-final-form'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { FinalFormInput, Option } from 'src/shared/form/control/index'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { Box } from '@mui/material'

interface AutocompleteFieldOption extends Omit<Option, 'value'> {
  value: number | string | boolean | null
}

type MuiAutoCompletePropsToBeExcluded = FinalFormInput | 'renderInput' | 'getOptionValue'

type AutoCompleteFieldProps = Omit<
  MuiAutoCompleteProps<AutocompleteFieldOption, false, boolean, false>,
  MuiAutoCompletePropsToBeExcluded
> & {
  name: string
  label: string
  options: AutocompleteFieldOption[]
  validate?: any
  required?: boolean
  onCustomChange?: (value: any) => boolean
  variant?: 'standard' | 'filled'
  renderOptionAsHtml?: boolean
  renderOptionLabelPrefix?: (option: AutocompleteFieldOption) => ReactElement
}

export const AutoCompleteField = (props: AutoCompleteFieldProps): ReactElement => {
  const {
    name,
    validate,
    label,
    options,
    required,
    onCustomChange,
    variant = 'filled',
    renderOptionAsHtml = false,
    renderOptionLabelPrefix,
    ...rest
  } = props
  const { getMessage } = useMessageSource()

  const defaultRenderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AutocompleteFieldOption) => {
    if (renderOptionAsHtml) {
      return (
        <li {...props}>
          <Box display="flex" flexDirection="row">
            {renderOptionLabelPrefix && (
              <Box marginTop={(theme) => theme.spacing(0.5)}>{renderOptionLabelPrefix(option)}</Box>
            )}
            <HtmlRenderer html={option.label} color={'textPrimary'} />
          </Box>
        </li>
      )
    }

    return (
      <li {...props}>
        <Box display="flex" flexDirection="row" margin={0} padding={0}>
          {renderOptionLabelPrefix && renderOptionLabelPrefix(option)}
          {option.label}
        </Box>
      </li>
    )
  }

  return (
    <Field
      name={name}
      allowNull
      validate={validate}
      render={({ input: { onFocus, onBlur, onChange, value }, meta: { touched, error: errorObject } }) => {
        const onChangeLocal = (_: any, optionValue: any): void => {
          if (onCustomChange) {
            const confirmation = onCustomChange(optionValue?.value ?? null)
            if (confirmation) {
              onChange(optionValue?.value ?? null)
            }
          } else {
            onChange(optionValue?.value ?? null)
          }
        }

        // react-final-form for a Field that doesn't have an initialValue will always initialize it as an empty string
        // treat the empty string as the nullable value from the options array
        const resolvedOptionValue = options.find((x) => x.value === value || (value === '' && x.value === null)) || null
        const error = errorObject && getMessage(errorObject.errorKey, errorObject.params)
        const invalid = Boolean(touched && error)

        return (
          <MuiAutoComplete
            {...rest}
            onBlur={onBlur}
            options={options}
            onFocus={onFocus}
            getOptionLabel={(option: any): string =>
              renderOptionAsHtml ? HtmlSanitizer.stripHtmlTags(option?.label ?? '') : option?.label ?? ''
            }
            getOptionDisabled={(option) => option.disabled ?? false}
            value={resolvedOptionValue}
            onChange={onChangeLocal}
            renderOption={defaultRenderOption}
            multiple={false}
            renderInput={(params: MuiAutoCompleteRenderInputParams): ReactNode => (
              <TextField
                {...params}
                name={name}
                required={required}
                fullWidth
                label={label}
                onBlur={onBlur}
                error={invalid}
                variant={variant}
                helperText={(invalid && error) || ' '}
              />
            )}
          />
        )
      }}
    />
  )
}
